// =====================================================================
// ASISTENTE DEL SEGUNDO FACTOR · ESTABILIDAD Parte D · D1 + D2
// =====================================================================
// Aparece al entrar a quien le hace falta (dueño y administradores
// siempre; el personal si su empresa lo pide). Cuatro pasos, uno por
// pantalla y con el avance a la vista, para que nadie se quede delante de
// un formulario con un QR, un campo y diez códigos a la vez:
//
//   1 · Qué es y qué necesitas     2 · Escanea el código
//   3 · Escribe un código de prueba  4 · Guarda tus códigos de respaldo
//
// Se puede dejar «para después» mientras la empresa no lo exija: el
// servidor es quien bloquea de verdad, y lo hace solo con la política SI.
// Aquí no se decide quién puede entrar; se acompaña a configurarlo.
import { useContext, useEffect, useRef, useState } from 'react';
import { useCambiosSinGuardar, usePaso } from '../ui/navegacionReact';
import { ShieldCheck, Smartphone, KeyRound, Printer, Loader2, CheckCircle2 } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import { ContextoAuth } from '../lib/contextoAuth';
import { PASOS, soloDigitos } from './segundoFactor';
// Peaje U-02 · legible en cualquier aparato: piezas de presentación.
import MarcoQR from '../peaje/MarcoQR';
import BotonCopiar from '../peaje/BotonCopiar';
import HojaDeCodigos from '../peaje/HojaDeCodigos';
import { imprimirHoja } from '../peaje/hojaDeCodigos';
import { agruparDeCuatro, cuentaDelEnlace } from '../peaje/copiar';

// Clases de Peaje U-02: texto de lectura a 16 px y ≤ 70 caracteres por
// renglón; botones primarios de ancho completo en el teléfono.
const LECTURA = 'text-base leading-relaxed max-w-[65ch] text-gray-700';
const PRIMARIO = 'inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-marca)] px-5 py-2 text-base font-semibold text-white disabled:opacity-60 sm:w-auto';
const SECUNDARIO = 'inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-base sm:w-auto';
// La ayuda de capa 2 se pinta a 12 px por omisión; dentro del asistente sube a 16.
const AYUDA_LEGIBLE = '[&>span]:text-base [&>span]:leading-relaxed max-w-[65ch]';

function Avance({ paso }) {
  return (
    <div>
      <p className="sr-only" aria-live="polite">
        Paso {paso + 1} de {PASOS.length}: {PASOS[paso]}
      </p>
      <ol aria-label="Avance" className="grid grid-cols-4 gap-1.5">
        {PASOS.map((p, i) => (
          <li key={p} aria-current={i === paso ? 'step' : undefined} className="min-w-0">
            <span aria-hidden="true" className={`block h-1.5 rounded-full ${i <= paso ? 'bg-[var(--color-marca)]' : 'bg-gray-200'}`} />
            <span className={`mt-1 block break-words text-xs leading-tight min-[480px]:text-sm ${i === paso ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
              {i === paso ? <span>{i + 1}. </span> : null}{p}
              {i < paso ? <span className="sr-only"> ✓ hecho</span> : null}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function AsistenteSegundoFactor({ onTerminar, onPosponer }) {
  const [paso, setPaso] = useState(0);
  // Atrás vuelve al paso anterior. El paso 3 (ya activado) no es un paso al
  // que se regrese ni del que se regrese al código: al llegar a él, las
  // entradas del asistente se consumen.
  usePaso('segundo-factor', paso === 3 ? 0 : paso, setPaso);
  const [preparado, setPreparado] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [codigos, setCodigos] = useState([]);
  const [guardados, setGuardados] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  // Peaje U-02: refs de presentación (foco al cambiar de paso, campo, lista).
  const titulo = useRef(null);
  const campo = useRef(null);
  const lista = useRef(null);
  const clave = useRef(null);
  const sesion = useContext(ContextoAuth);
  const primerPaso = useRef(true);
  useEffect(() => {
    if (primerPaso.current) { primerPaso.current = false; return; }
    titulo.current?.focus();
  }, [paso]);
  // Con los códigos de respaldo a la vista y sin marcar «Ya los guardé»,
  // atrás pregunta antes de salir (TC-DR-AT).
  useCambiosSinGuardar(() => paso === 3 && !guardados, {
    detalle: 'Los códigos de respaldo no se vuelven a mostrar. Guárdalos antes de salir.',
  });

  async function empezar() {
    setCargando(true);
    setError(null);
    try {
      setPreparado(await api.post('/auth/2fa/preparar', {}));
      setPaso(1);
    } catch (e) {
      setError(e.message || 'No se pudo preparar el código. Revisa la conexión y vuelve a intentarlo.');
    } finally {
      setCargando(false);
    }
  }

  async function probar() {
    setCargando(true);
    setError(null);
    try {
      const r = await api.post('/auth/2fa/activar', { codigo });
      setCodigos(r.codigos_de_respaldo || []);
      setPaso(3);
    } catch (e) {
      setError(e.message || 'Ese código no coincide. Escribe los seis dígitos que muestra tu aplicación ahora.');
      // Peaje U-02: lo escrito NO se borra; el foco vuelve al campo, seleccionado.
      setTimeout(() => { campo.current?.focus(); campo.current?.select(); }, 0);
    } finally {
      setCargando(false);
    }
  }

  const listo = soloDigitos(codigo).length === 6;
  const cuenta = cuentaDelEnlace(preparado?.enlace) || sesion?.usuario?.nombre || '';
  const tituloDePaso = (texto) => (
    <h2 ref={titulo} tabIndex={-1} className="text-lg font-semibold text-gray-900 outline-none">{texto}</h2>
  );

  return (
    // Peaje U-02: `consola` + el lienzo del tema, como CambioPassword: el asistente se
    // pinta fuera de AppShell y sin esto no seguía data-tema="oscuro".
    <div className="consola min-h-dvh bg-[var(--color-lienzo)] px-4 py-6 text-gray-800 sm:py-12">
      <div className="mx-auto max-w-lg min-w-0 space-y-5 rounded-2xl border border-gray-200 bg-white p-5 sm:p-7">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-7 w-7 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">Protege tu cuenta con un segundo factor</h1>
            <p className="text-base text-gray-600">Además de tu contraseña, TEGO te pedirá un código de tu teléfono.</p>
          </div>
        </div>
        <Ayuda id="seguridad.asistente_2fa.pantalla" />
        <Avance paso={paso} />

        {paso === 0 && (
          <div className="space-y-3">
            {tituloDePaso('Qué es y qué necesitas')}
            <div className="flex min-w-0 gap-2">
              <Smartphone className="mt-1 h-5 w-5 shrink-0 text-gray-700" aria-hidden="true" />
              <p className={`min-w-0 ${LECTURA}`}>
                Necesitas en tu teléfono una aplicación de códigos de seis dígitos. Sirve cualquiera, por ejemplo Google Authenticator o Microsoft Authenticator, y son gratis.
              </p>
            </div>
            <p className={LECTURA}>Tarda unos dos minutos. Al final TEGO te da diez códigos de respaldo para entrar si pierdes el teléfono.</p>
            <div className={AYUDA_LEGIBLE}><Ayuda id="seguridad.asistente_2fa.que_es" /></div>
          </div>
        )}

        {paso === 1 && preparado && (
          <div className="space-y-3">
            {tituloDePaso('Escanea el código')}
            <p className={LECTURA}>Abre tu aplicación de códigos, elige «Agregar cuenta» y escanea este código con la cámara.</p>
            <div className="flex justify-center">
              <MarcoQR valor={preparado.enlace} etiqueta="Código para tu aplicación de autenticación" />
            </div>
            <div className={AYUDA_LEGIBLE}><Ayuda id="seguridad.asistente_2fa.qr" /></div>
            <div className="min-w-0 space-y-2 rounded-lg border border-gray-200 p-3">
              <p className="text-base font-medium text-gray-900">Si no puedes escanear, escribe esta clave</p>
              <code ref={clave} className="block select-all break-all rounded bg-gray-100 p-2 font-mono text-base tracking-wider text-gray-900">
                {agruparDeCuatro(preparado.secreto)}
              </code>
              <div className={AYUDA_LEGIBLE}><Ayuda id="seguridad.asistente_2fa.clave_manual" /></div>
              <BotonCopiar texto={preparado.secreto} objetivo={clave} etiqueta="Copiar la clave" confirmacion="Clave copiada" />
            </div>
          </div>
        )}

        {paso === 2 && (
          <form id="form-codigo-prueba" className="space-y-2"
                onSubmit={(e) => { e.preventDefault(); if (listo && !cargando) probar(); }}>
            {tituloDePaso('Escribe un código de prueba')}
            {/* Sin maxLength a propósito: el navegador recortaría «123 456» pegado a «123 45»
                antes de limpiarlo; soloDigitos quita lo que sobra y corta a 6 después. */}
            <label htmlFor="codigo-prueba" className="block text-base font-medium text-gray-800">
              Código de prueba <span className="text-red-600">*</span>
            </label>
            <input
              ref={campo}
              id="codigo-prueba" type="text" inputMode="numeric" autoComplete="one-time-code"
              pattern="[0-9]{6}" enterKeyHint="done"
              aria-describedby="ayuda-codigo error-codigo" aria-invalid={!!error}
              value={codigo} onChange={(e) => setCodigo(soloDigitos(e.target.value))}
              placeholder="123456"
              className="w-full rounded-lg border border-gray-300 px-3 py-3 text-center font-mono text-2xl tracking-[0.4em]"
            />
            <div id="ayuda-codigo" className={AYUDA_LEGIBLE}><Ayuda id="seguridad.asistente_2fa.codigo_prueba" /></div>
          </form>
        )}

        {paso === 3 && (
          <div className="space-y-3">
            {tituloDePaso('Guarda tus códigos de respaldo')}
            <p className="flex items-center gap-2 text-base font-medium text-green-800">
              <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" /> Listo: tu segundo factor ya está activo.
            </p>
            <p className={LECTURA}>Estos son tus 10 códigos de respaldo:</p>
            <ul ref={lista} className="grid grid-cols-1 gap-2 rounded-lg bg-gray-100 p-3 min-[480px]:grid-cols-2">
              {codigos.map((c) => <li key={c} className="min-w-0 break-all font-mono text-base tracking-wide text-gray-900">{c}</li>)}
            </ul>
            <div className={AYUDA_LEGIBLE}><Ayuda id="seguridad.asistente_2fa.codigos_respaldo" /></div>
            <HojaDeCodigos codigos={codigos} cuenta={cuenta} />
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <button type="button" onClick={imprimirHoja} className={SECUNDARIO}>
                <Printer className="h-5 w-5 shrink-0" aria-hidden="true" /> Imprimir
              </button>
              <BotonCopiar texto={codigos.join('\n')} objetivo={lista} etiqueta="Copiar" confirmacion="Copiados" />
            </div>
            <div className={AYUDA_LEGIBLE}><Ayuda id="seguridad.asistente_2fa.imprimir" /></div>
            <div className={AYUDA_LEGIBLE}><Ayuda id="seguridad.asistente_2fa.copiar" /></div>
            <label className="flex min-h-12 items-center gap-3 text-base text-gray-800">
              <input type="checkbox" checked={guardados} onChange={(e) => setGuardados(e.target.checked)}
                     aria-describedby={guardados ? undefined : 'motivo-terminar'} className="h-6 w-6 shrink-0" />
              Ya los guardé en un lugar seguro
            </label>
          </div>
        )}

        {error && <p id="error-codigo" role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-base text-red-700">{error}</p>}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          {paso === 0 && (
            <button type="button" onClick={empezar} disabled={cargando} className={PRIMARIO}>
              {cargando && <Loader2 className="h-5 w-5 motion-safe:animate-spin" aria-hidden="true" />}
              Empezar
            </button>
          )}
          {paso === 1 && (
            <button type="button" onClick={() => setPaso(2)} className={PRIMARIO}>
              Ya lo escaneé
            </button>
          )}
          {paso === 2 && (
            <div className="flex flex-col items-stretch sm:items-start">
              <button type="submit" form="form-codigo-prueba" disabled={!listo || cargando} aria-describedby={listo ? undefined : 'motivo-comprobar'} className={PRIMARIO}>
                {cargando && <Loader2 className="h-5 w-5 motion-safe:animate-spin" aria-hidden="true" />}
                <KeyRound className="h-5 w-5 shrink-0" aria-hidden="true" /> Comprobar código
              </button>
              {!listo && <p id="motivo-comprobar" className="mt-1 text-sm text-gray-600">Se enciende cuando escribas los 6 dígitos.</p>}
            </div>
          )}
          {paso === 3 && (
            <div className="flex flex-col items-stretch sm:items-start">
              <button type="button" onClick={onTerminar} disabled={!guardados} aria-describedby={guardados ? undefined : 'motivo-terminar'} className={PRIMARIO}>
                Terminar y entrar a TEGO
              </button>
              {!guardados && <p id="motivo-terminar" className="mt-1 text-sm text-gray-600">Marca «Ya los guardé» para continuar: no se vuelven a mostrar.</p>}
            </div>
          )}
          {paso < 3 && onPosponer ? (
            <button type="button" onClick={onPosponer}
                    className="min-h-12 w-full rounded-lg px-3 py-2 text-base text-gray-600 hover:bg-gray-100 sm:w-auto">
              Hacerlo después
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
